import loaded from '@/scripts/utils/loaded.js';

// Minimal replacement for the old tooltip plugin setup that used to live here:
// - content comes from the element referenced by `data-tooltip-content` (falls back to `title`)
// - opens on mouseenter / touchstart (and keyboard focus), closes on any click / tap, blur or Escape
// - prefers to sit above the trigger, flips below when there is no room, max width 400px
const GAP = 10; // distance between the trigger and the tooltip (arrow included)
const VIEWPORT_MARGIN = 8;
const MAX_WIDTH = 400;

let uid = 0;
let openTooltip = null;

function createTooltip(trigger) {
    const tooltip = document.createElement('div');
    tooltip.className = 'hr-tooltip hr-tooltip--top';
    tooltip.id = `hr-tooltip-${++uid}`;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.hidden = true;
    tooltip.innerHTML = '<div class="hr-tooltip__box"><div class="hr-tooltip__content"></div></div><span class="hr-tooltip__arrow"></span>';

    const content = tooltip.querySelector('.hr-tooltip__content');
    const selector = trigger.getAttribute('data-tooltip-content');
    const source = selector ? document.querySelector(selector) : null;

    if (source) {
        // Tooltipster moved (not cloned) the content node, which keeps id-based styles (#smells-tooltip) working.
        content.appendChild(source);
    } else if (trigger.getAttribute('title')) {
        content.textContent = trigger.getAttribute('title');
        trigger.removeAttribute('title');
    } else {
        return null;
    }

    document.body.appendChild(tooltip);
    trigger.setAttribute('aria-describedby', tooltip.id);

    return tooltip;
}

function position(trigger, tooltip) {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;

    tooltip.style.maxWidth = `${Math.min(MAX_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2)}px`;
    // Measure unconstrained: a stale `left` near the right edge would shrink-to-fit the box.
    tooltip.style.left = '0px';

    const {offsetWidth: width, offsetHeight: height} = tooltip;
    const placeBelow = rect.top - height - GAP < 0 && rect.bottom + height + GAP <= window.innerHeight;

    tooltip.classList.toggle('hr-tooltip--top', !placeBelow);
    tooltip.classList.toggle('hr-tooltip--bottom', placeBelow);

    const center = rect.left + rect.width / 2;
    const left = Math.min(
        Math.max(center - width / 2, VIEWPORT_MARGIN),
        Math.max(viewportWidth - width - VIEWPORT_MARGIN, VIEWPORT_MARGIN)
    );
    const top = placeBelow ? rect.bottom + GAP : rect.top - height - GAP;

    tooltip.style.left = `${left + window.scrollX}px`;
    tooltip.style.top = `${top + window.scrollY}px`;

    const arrow = tooltip.querySelector('.hr-tooltip__arrow');
    if (arrow) {
        arrow.style.left = `${Math.min(Math.max(center - left, 12), width - 12)}px`;
    }
}

function close() {
    if (!openTooltip) return;
    const {tooltip} = openTooltip;
    tooltip.classList.remove('is-visible');
    tooltip.hidden = true;
    openTooltip = null;
}

function init(trigger) {
    let tooltip = null;

    const open = () => {
        if (!tooltip) tooltip = createTooltip(trigger);
        if (!tooltip) return;
        if (openTooltip && openTooltip.tooltip !== tooltip) close();

        tooltip.hidden = false;
        position(trigger, tooltip);
        requestAnimationFrame(() => tooltip.classList.add('is-visible'));
        openTooltip = {trigger, tooltip, openedAt: Date.now()};
    };

    // Make the trigger reachable by keyboard (it is usually a plain <img>).
    if (trigger.tabIndex < 0) trigger.tabIndex = 0;

    trigger.addEventListener('mouseenter', open);
    trigger.addEventListener('touchstart', open, {passive: true});
    trigger.addEventListener('focus', open);
    trigger.addEventListener('blur', close);
}

loaded(() => {
    const triggers = document.querySelectorAll('.tooltip');
    if (!triggers.length) return;

    triggers.forEach(init);

    // triggerClose: { click: true, tap: true } — any click/tap closes, except the one that just opened it.
    document.addEventListener('click', (event) => {
        if (!openTooltip) return;
        const {trigger, tooltip, openedAt} = openTooltip;
        if (tooltip.contains(event.target)) return;
        if (trigger.contains(event.target) && Date.now() - openedAt < 1000) return;
        close();
    });

    // iOS Safari doesn't dispatch `click` for taps on non-interactive content, so close on
    // an outside touch too. Touches on the trigger are left to its own touchstart (open).
    document.addEventListener('touchstart', (event) => {
        if (!openTooltip) return;
        const {trigger, tooltip} = openTooltip;
        if (tooltip.contains(event.target) || trigger.contains(event.target)) return;
        close();
    }, {passive: true});

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
    });

    window.addEventListener('resize', () => {
        if (openTooltip) position(openTooltip.trigger, openTooltip.tooltip);
    });
});
